import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { TagService } from "../../services/TagService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2Icon, PlusIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

export type TagScope = "request" | "session";
export type TagRuleType =
  | "phrase_match"
  | "regex"
  | "metadata"
  | "llm_classifier"
  | "composite"
  | "sequence";

export interface TagDefinition {
  id?: number;
  company_id?: number;
  intent_group_id?: number | null;
  name: string;
  description?: string;
  scope: TagScope;
  rule_type: TagRuleType;
  rule_config: Record<string, any>;
  color?: string;
  is_base?: boolean;
  active?: boolean;
  date_created?: number;
  date_updated?: number;
  meta?: Record<string, any>;
}

const RULE_TYPE_LABELS: Record<TagRuleType, string> = {
  phrase_match: "Phrase Match",
  regex: "Regex",
  metadata: "Metadata Filter",
  llm_classifier: "LLM Classifier",
  composite: "Composite",
  sequence: "Sequence",
};

const TAG_COLORS = [
  "blue",
  "green",
  "red",
  "amber",
  "purple",
  "cyan",
  "indigo",
  "pink",
  "teal",
  "orange",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (saved: TagDefinition) => void;
  tag?: TagDefinition | null;
  existingTags?: TagDefinition[];
}

const TagDefinitionModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  tag,
  existingTags = [],
}) => {
  const { apiKey } = usePermissions();
  const isEditing = !!tag?.id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState<TagScope>("request");
  const [ruleType, setRuleType] = useState<TagRuleType>("phrase_match");
  const [color, setColor] = useState("blue");
  const [saving, setSaving] = useState(false);

  // phrase_match config
  const [phrases, setPhrases] = useState<string[]>([""]);
  const [phraseMatchMode, setPhraseMatchMode] = useState<"any" | "all">("any");
  const [phraseCaseSensitive, setPhraseCaseSensitive] = useState(false);
  const [phraseRole, setPhraseRole] = useState<string>("any");

  // regex config
  const [regexPattern, setRegexPattern] = useState("");
  const [regexRole, setRegexRole] = useState<string>("any");

  // metadata config
  const [metaField, setMetaField] = useState("");
  const [metaOperator, setMetaOperator] = useState("eq");
  const [metaValue, setMetaValue] = useState("");

  // llm_classifier config
  const [llmInstruction, setLlmInstruction] = useState("");
  const [llmModel, setLlmModel] = useState("gpt-4o-mini");

  // composite config
  const [compositeLogic, setCompositeLogic] = useState<"all" | "any" | "not">(
    "all"
  );
  const [compositeTagRefs, setCompositeTagRefs] = useState<number[]>([]);

  // sequence config
  const [sequenceSteps, setSequenceSteps] = useState<
    { tag_ref: number; role: string; within_turns?: number }[]
  >([]);
  const [sequenceAllowGaps, setSequenceAllowGaps] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (tag) {
      setName(tag.name);
      setDescription(tag.description || "");
      setScope(tag.scope);
      setRuleType(tag.rule_type);
      setColor(tag.color || "blue");
      populateFromConfig(tag.rule_type, tag.rule_config);
    } else {
      resetForm();
    }
  }, [open, tag]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setScope("request");
    setRuleType("phrase_match");
    setColor("blue");
    setPhrases([""]);
    setPhraseMatchMode("any");
    setPhraseCaseSensitive(false);
    setPhraseRole("any");
    setRegexPattern("");
    setRegexRole("any");
    setMetaField("");
    setMetaOperator("eq");
    setMetaValue("");
    setLlmInstruction("");
    setLlmModel("gpt-4o-mini");
    setCompositeLogic("all");
    setCompositeTagRefs([]);
    setSequenceSteps([]);
    setSequenceAllowGaps(true);
  };

  const populateFromConfig = (
    type: TagRuleType,
    config: Record<string, any>
  ) => {
    switch (type) {
      case "phrase_match":
        setPhrases(config.phrases?.length ? config.phrases : [""]);
        setPhraseMatchMode(config.match_mode || "any");
        setPhraseCaseSensitive(config.case_sensitive || false);
        setPhraseRole(config.role || "any");
        break;
      case "regex":
        setRegexPattern(config.pattern || "");
        setRegexRole(config.role || "any");
        break;
      case "metadata":
        setMetaField(config.field || "");
        setMetaOperator(config.operator || "eq");
        setMetaValue(config.value || "");
        break;
      case "llm_classifier":
        setLlmInstruction(config.instruction || "");
        setLlmModel(config.model || "gpt-4o-mini");
        break;
      case "composite":
        setCompositeLogic(config.logic || "all");
        setCompositeTagRefs(config.tag_refs || []);
        break;
      case "sequence":
        setSequenceSteps(config.steps || []);
        setSequenceAllowGaps(config.allow_gaps !== false);
        break;
    }
  };

  const buildConfig = (): Record<string, any> => {
    switch (ruleType) {
      case "phrase_match":
        return {
          phrases: phrases.filter((p) => p.trim()),
          match_mode: phraseMatchMode,
          case_sensitive: phraseCaseSensitive,
          role: phraseRole === "any" ? undefined : phraseRole,
        };
      case "regex":
        return {
          pattern: regexPattern,
          role: regexRole === "any" ? undefined : regexRole,
        };
      case "metadata":
        return { field: metaField, operator: metaOperator, value: metaValue };
      case "llm_classifier":
        return { instruction: llmInstruction, model: llmModel };
      case "composite":
        return { logic: compositeLogic, tag_refs: compositeTagRefs };
      case "sequence":
        return {
          steps: sequenceSteps,
          allow_gaps: sequenceAllowGaps,
          require_order: true,
        };
      default:
        return {};
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    const payload: any = {
      name: name.trim(),
      description: description.trim() || undefined,
      scope,
      rule_type: ruleType,
      rule_config: buildConfig(),
      color,
      is_base: !["composite", "sequence"].includes(ruleType),
    };

    const action = isEditing
      ? TagService.update(tag!.id!, payload, setSaving, apiKey)
      : TagService.create(payload, setSaving, apiKey);

    action.subscribe(
      (saved: TagDefinition) => {
        toast.success(isEditing ? "Tag updated" : "Tag created");
        onSave(saved);
        setSaving(false);
      },
      (err: any) => {
        console.error("Failed to save tag", err);
        toast.error("Failed to save tag");
        setSaving(false);
      }
    );
  };

  const baseTags = existingTags.filter((t) => t.is_base && t.id !== tag?.id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Tag" : "New Tag"}</DialogTitle>
          <DialogDescription>
            Define a tagging rule to classify your data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. contains_apology"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>

          {/* Scope + Rule type row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={scope}
                onChange={(e) => setScope(e.target.value as TagScope)}
              >
                <option value="request">Request</option>
                <option value="session">Session (conversation)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Rule Type</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value as TagRuleType)}
              >
                {Object.entries(RULE_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {TAG_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full border-2 ${
                    color === c ? "border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: `var(--${c}-500, ${c})` }}
                  title={c}
                />
              ))}
            </div>
          </div>

          {/* Rule config */}
          <div className="border-t pt-4 space-y-3">
            <h3 className="text-sm font-gc-med text-muted-foreground">
              Rule Configuration
            </h3>

            {ruleType === "phrase_match" && (
              <PhraseMatchConfig
                phrases={phrases}
                setPhrases={setPhrases}
                matchMode={phraseMatchMode}
                setMatchMode={setPhraseMatchMode}
                caseSensitive={phraseCaseSensitive}
                setCaseSensitive={setPhraseCaseSensitive}
                role={phraseRole}
                setRole={setPhraseRole}
              />
            )}

            {ruleType === "regex" && (
              <RegexConfig
                pattern={regexPattern}
                setPattern={setRegexPattern}
                role={regexRole}
                setRole={setRegexRole}
              />
            )}

            {ruleType === "metadata" && (
              <MetadataConfig
                field={metaField}
                setField={setMetaField}
                operator={metaOperator}
                setOperator={setMetaOperator}
                value={metaValue}
                setValue={setMetaValue}
              />
            )}

            {ruleType === "llm_classifier" && (
              <LLMClassifierConfig
                instruction={llmInstruction}
                setInstruction={setLlmInstruction}
                model={llmModel}
                setModel={setLlmModel}
              />
            )}

            {ruleType === "composite" && (
              <CompositeConfig
                logic={compositeLogic}
                setLogic={setCompositeLogic}
                tagRefs={compositeTagRefs}
                setTagRefs={setCompositeTagRefs}
                baseTags={baseTags}
              />
            )}

            {ruleType === "sequence" && (
              <SequenceConfig
                steps={sequenceSteps}
                setSteps={setSequenceSteps}
                allowGaps={sequenceAllowGaps}
                setAllowGaps={setSequenceAllowGaps}
                baseTags={baseTags}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2Icon className="w-4 h-4 animate-spin mr-2" />}
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TagDefinitionModal;

/* ---------- Sub-config components ---------- */

const RoleSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <select
    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="any">Any role</option>
    <option value="user">User</option>
    <option value="assistant">Assistant</option>
    <option value="system">System</option>
  </select>
);

const PhraseMatchConfig: React.FC<{
  phrases: string[];
  setPhrases: (v: string[]) => void;
  matchMode: "any" | "all";
  setMatchMode: (v: "any" | "all") => void;
  caseSensitive: boolean;
  setCaseSensitive: (v: boolean) => void;
  role: string;
  setRole: (v: string) => void;
}> = ({
  phrases,
  setPhrases,
  matchMode,
  setMatchMode,
  caseSensitive,
  setCaseSensitive,
  role,
  setRole,
}) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Phrases</Label>
      {phrases.map((p, i) => (
        <div key={i} className="flex gap-2">
          <Input
            value={p}
            onChange={(e) => {
              const next = [...phrases];
              next[i] = e.target.value;
              setPhrases(next);
            }}
            placeholder="Enter phrase..."
          />
          {phrases.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPhrases(phrases.filter((_, j) => j !== i))}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setPhrases([...phrases, ""])}
      >
        <PlusIcon className="w-3 h-3 mr-1" /> Add phrase
      </Button>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1.5">
        <Label>Match</Label>
        <select
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          value={matchMode}
          onChange={(e) => setMatchMode(e.target.value as "any" | "all")}
        >
          <option value="any">Any phrase</option>
          <option value="all">All phrases</option>
        </select>
      </div>
      <div className="space-y-1.5">
        <Label>Role</Label>
        <RoleSelect value={role} onChange={setRole} />
      </div>
      <div className="space-y-1.5 flex items-end">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
          />
          Case sensitive
        </label>
      </div>
    </div>
  </div>
);

const RegexConfig: React.FC<{
  pattern: string;
  setPattern: (v: string) => void;
  role: string;
  setRole: (v: string) => void;
}> = ({ pattern, setPattern, role, setRole }) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Pattern</Label>
      <Input
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        placeholder="e.g. \\bHS\\d{4,10}\\b"
        className="font-mono text-xs"
      />
    </div>
    <div className="space-y-1.5">
      <Label>Role</Label>
      <RoleSelect value={role} onChange={setRole} />
    </div>
  </div>
);

const MetadataConfig: React.FC<{
  field: string;
  setField: (v: string) => void;
  operator: string;
  setOperator: (v: string) => void;
  value: string;
  setValue: (v: string) => void;
}> = ({ field, setField, operator, setOperator, value, setValue }) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Field path</Label>
      <Input
        value={field}
        onChange={(e) => setField(e.target.value)}
        placeholder="e.g. meta.model or _metadata.tags"
        className="font-mono text-xs"
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label>Operator</Label>
        <select
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
        >
          <option value="eq">Equals</option>
          <option value="neq">Not equals</option>
          <option value="contains">Contains</option>
          <option value="not_contains">Does not contain</option>
          <option value="gt">Greater than</option>
          <option value="lt">Less than</option>
          <option value="exists">Exists</option>
          <option value="not_exists">Does not exist</option>
        </select>
      </div>
      <div className="space-y-1.5">
        <Label>Value</Label>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Value to compare"
        />
      </div>
    </div>
  </div>
);

const LLMClassifierConfig: React.FC<{
  instruction: string;
  setInstruction: (v: string) => void;
  model: string;
  setModel: (v: string) => void;
}> = ({ instruction, setInstruction, model, setModel }) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Instruction</Label>
      <textarea
        className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm min-h-[80px]"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder="e.g. Does this assistant message contain a refusal to help?"
      />
    </div>
    <div className="space-y-1.5">
      <Label>Model</Label>
      <select
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
        value={model}
        onChange={(e) => setModel(e.target.value)}
      >
        <option value="gpt-4o-mini">gpt-4o-mini</option>
        <option value="gpt-4o">gpt-4o</option>
      </select>
    </div>
  </div>
);

const CompositeConfig: React.FC<{
  logic: "all" | "any" | "not";
  setLogic: (v: "all" | "any" | "not") => void;
  tagRefs: number[];
  setTagRefs: (v: number[]) => void;
  baseTags: TagDefinition[];
}> = ({ logic, setLogic, tagRefs, setTagRefs, baseTags }) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Logic</Label>
      <select
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
        value={logic}
        onChange={(e) => setLogic(e.target.value as "all" | "any" | "not")}
      >
        <option value="all">ALL referenced tags present</option>
        <option value="any">ANY referenced tag present</option>
        <option value="not">NONE of referenced tags present</option>
      </select>
    </div>
    <div className="space-y-1.5">
      <Label>Referenced Tags</Label>
      {baseTags.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No base tags available. Create simple rules first.
        </p>
      ) : (
        <div className="space-y-1 max-h-[200px] overflow-y-auto border rounded-md p-2">
          {baseTags.map((bt) => (
            <label key={bt.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={tagRefs.includes(bt.id!)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTagRefs([...tagRefs, bt.id!]);
                  } else {
                    setTagRefs(tagRefs.filter((id) => id !== bt.id));
                  }
                }}
              />
              {bt.name}
            </label>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SequenceConfig: React.FC<{
  steps: { tag_ref: number; role: string; within_turns?: number }[];
  setSteps: (
    v: { tag_ref: number; role: string; within_turns?: number }[]
  ) => void;
  allowGaps: boolean;
  setAllowGaps: (v: boolean) => void;
  baseTags: TagDefinition[];
}> = ({ steps, setSteps, allowGaps, setAllowGaps, baseTags }) => (
  <div className="space-y-3">
    <div className="space-y-1.5">
      <Label>Sequence Steps</Label>
      {steps.map((step, i) => (
        <div key={i} className="flex gap-2 items-center">
          <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
          <select
            className="flex h-9 flex-1 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={step.tag_ref || ""}
            onChange={(e) => {
              const next = [...steps];
              next[i] = { ...next[i], tag_ref: parseInt(e.target.value) };
              setSteps(next);
            }}
          >
            <option value="">Select tag...</option>
            {baseTags.map((bt) => (
              <option key={bt.id} value={bt.id}>
                {bt.name}
              </option>
            ))}
          </select>
          <select
            className="flex h-9 w-28 rounded-md border border-input bg-transparent px-2 py-1 text-sm"
            value={step.role || "any"}
            onChange={(e) => {
              const next = [...steps];
              next[i] = { ...next[i], role: e.target.value };
              setSteps(next);
            }}
          >
            <option value="any">Any</option>
            <option value="user">User</option>
            <option value="assistant">Assistant</option>
          </select>
          <Input
            className="w-20"
            type="number"
            placeholder="turns"
            value={step.within_turns ?? ""}
            onChange={(e) => {
              const next = [...steps];
              next[i] = {
                ...next[i],
                within_turns: e.target.value ? parseInt(e.target.value) : undefined,
              };
              setSteps(next);
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSteps(steps.filter((_, j) => j !== i))}
          >
            <XIcon className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          setSteps([...steps, { tag_ref: 0, role: "any" }])
        }
      >
        <PlusIcon className="w-3 h-3 mr-1" /> Add step
      </Button>
    </div>
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={allowGaps}
        onChange={(e) => setAllowGaps(e.target.checked)}
      />
      Allow extra messages between steps
    </label>
  </div>
);
